import React, { useContext } from "react";
import { overrideClass, TKUIWithStyle, useStyles } from "tripkit-react/dist/jss/StyleHelper";
import riderCategoriesSettingDefaultStyle from "./RiderCategoriesSetting.css";
import TKUserAccount from "tripkit-react/dist/account/TKUserAccount";
import TKUISettingSection from "tripkit-react/dist/options/TKUISettingSection";
import { ClientContext } from "../client/ClientProvider";
import TKUICheckbox from "tripkit-react/dist/util_components/TKUICheckbox";
import { useI18n } from "tripkit-react/dist/i18n/TKI18nProvider";
import TKUIRow from "tripkit-react/dist/options/TKUIRow";
import { i18n } from "tripkit-react/dist/i18n/TKI18nConstants";

type IStyle = ReturnType<typeof riderCategoriesSettingDefaultStyle>;
type Category = NonNullable<NonNullable<Client["profile"]>["riderCategories"]>[number];

interface IProps extends TKUIWithStyle<IStyle, IProps> {
    user: TKUserAccount;
    onUserChange?: (user: TKUserAccount) => void;
    readonly?: boolean;
}

const RiderCategoriesSetting: React.FunctionComponent<IProps> = (props: IProps) => {
    const { readonly = false } = props;
    const { classes, theme } = useStyles(props, riderCategoriesSettingDefaultStyle);
    const { t, locale } = useI18n();
    const { client } = useContext(ClientContext);

    function handleCategoryCheck(checked: boolean, category: Category) {
        const currentCategories = props.user.appData?.categories || [];
        const updatedCategories = checked
            ? [...currentCategories, category.id]
            : currentCategories.filter((id) => id !== category.id);
        const appDataUpdate = { ...(props.user.appData ?? {}), categories: updatedCategories };
        const updatedUser = { ...props.user, appData: { ...appDataUpdate } };
        props.onUserChange?.(updatedUser);
    }

    if (!client?.profile?.riderCategories) {
        return null;
    }
    if (readonly) {
        const selectedCategories = client.profile.riderCategories
            .filter(category => props.user.appData?.categories?.includes(category.id))
            .map(category => category.translations?.[locale] || category.displayName);
        return (
            <TKUISettingSection styles={{ sectionTitle: overrideClass({ paddingLeft: '45px' }), sectionBody: overrideClass({ borderTop: 'none', marginTop: '-10px' }) }}>
                <TKUIRow
                    title="Rider Categories"
                    subtitle={selectedCategories.length > 0 ? selectedCategories.join(", ") : "-"}
                />
            </TKUISettingSection>
        );
    }
    return (
        <TKUISettingSection title="Rider Categories" styles={{ sectionTitle: overrideClass({ paddingLeft: '45px' }) }}>
            {client.profile.riderCategories.map((category) => {
                const checked = props.user.appData?.categories?.includes(category.id);
                return (
                    <div key={category.id} className={classes.riderCategory}>
                        <label>
                            {category.translations?.[locale] || category.displayName}
                        </label>
                        <TKUICheckbox theme={theme} checked={checked} onChange={(e) => handleCategoryCheck(e.target.checked, category)} />
                    </div>
                );
            })}
            <div className={classes.disclaimer}>
                {client?.uiConfig?.messages?.categoryDescription?.translations?.[i18n.locale] || client?.uiConfig?.messages?.categoryDescription?.text}
            </div>
        </TKUISettingSection>
    );
};

export default RiderCategoriesSetting;
